import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../assets/css/common.css';
import '../../assets/css/main.css';
import '../../assets/css/adm_custom.css';
import 'summernote/dist/summernote-bs4.css';
import $ from 'jquery';
import 'summernote/dist/summernote-bs4.js';
import { useSummernoteImageUpload } from '../../utils/summernote';
import { openAlert, openConfirm } from '../../utils/common';
import { POPUP_IMAGE_PATH } from '../../utils/common';
import { useValidation, useSecurityMonitor } from '../../hooks/useValidation';
import { sanitizeApiRequest, sanitizeString } from '../../utils/xssProtection';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import popupService from '../../services/popupService';

const PopupFormPage = () => {
  const { action, id } = useParams(); // action: write or edit
  const isEdit = action === 'edit';
  const navigate = useNavigate();
  const { uploadImage } = useSummernoteImageUpload();
  const [useStateVal, setUseStateVal] = useState(1);
  const [link, setLink] = useState('');
  const [titleKo, setTitleKo] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedLang, setSelectedLang] = useState('ko');
  const [addSummerNoteImages, setAddSummerNoteImages] = useState([]);
  const [delSummerNoteImages, setDelSummerNoteImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);

  // 보안 모니터링 훅
  const {
    securityLog,
    riskLevel,
    logSecurityEvent,
    clearSecurityLog,
    eventCount
  } = useSecurityMonitor();


  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const result = await popupService.getPopupDetail(id);
        
        if (result.success && result.data) {
          const val = result.data;
          
          setUseStateVal(Number(val.use_state) || 0);
          setLink(val.link || '');
          
          // null, undefined, 빈 문자열 모두 대응하도록 처리
          const titleKoVal = val.title_ko !== null && val.title_ko !== undefined ? val.title_ko : '';
          const titleEnVal = val.title_en !== null && val.title_en !== undefined ? val.title_en : '';
          
          // 데이터 로드 시에도 XSS 방지를 위한 정화 적용
          setTitleKo(sanitizeString(titleKoVal, 'text'));
          setTitleEn(sanitizeString(titleEnVal, 'text'));
          $('#summernote_ko').summernote('code', sanitizeString(val.content_ko || '', 'adminEditor'));
          $('#summernote_en').summernote('code', sanitizeString(val.content_en || '', 'adminEditor'));
          setAddSummerNoteImages(JSON.parse(val.addSummerNoteImageArray || '[]'));
          setDelSummerNoteImages(JSON.parse(val.delSummerNoteImageArray || '[]'));
        } else {
          openAlert({title:"알림", description: result.message});
        }
      } catch(e){ 
        console.error(e); 
        openAlert({title:"알림", description: '데이터 로드 중 오류가 발생했습니다.'}); 
      } finally { 
        setLoading(false); 
      }
    };

    if (isEdit) fetchDetail();
    const options = {
      placeholder: isEdit ? '' : '',
      height: 500,
      lang: 'ko-KR',
      callbacks: {
        onImageUpload: async files => {
          for (let file of files) {
            try {
              const { filename, url } = await uploadImage(file, POPUP_IMAGE_PATH);
              $('#summernote_ko').summernote('editor.insertImage', url);
              setAddSummerNoteImages(prev => [...prev, filename]);
            } catch (e) { console.error(e); openAlert({title:"알림", description: e.message}); }
          }
        },
        onMediaDelete: function(target) {
          const src = target[0].src || target[0].href;
          const filename = src.substring(src.lastIndexOf('/') + 1);
          setDelSummerNoteImages(prev => [...prev, filename]);
        }
      }
    };
    $('#summernote_ko').summernote({ placeholder: '한국어 내용을 입력해주세요.', ...options });
    $('#summernote_en').summernote({ placeholder: 'Please enter content in English', ...options });
  }, [isEdit, id]);

  /**
   * 팝업 데이터 검증
   */
  const validatePopupData = useCallback(() => {
    const newErrors = {};

    // 최소 한 개의 언어 버전이 있는지 확인
    const hasKoreanVersion =
      titleKo.trim() &&
      $("#summernote_ko").summernote("code") !== "<p><br></p>";
    const hasEnglishVersion =
      titleEn.trim() &&
      $("#summernote_en").summernote("code") !== "<p><br></p>";

    if (!hasKoreanVersion && !hasEnglishVersion) {
      newErrors.general = "최소 한 개의 언어 제목과 내용을 모두 입력해주세요.";
    }

    // 제목 길이 검증 (100자)
    if (titleKo.trim() && titleKo.length > 100) {
      newErrors.titleKo = "한국어 제목은 100자 이내로 입력해주세요.";
    }
    if (titleEn.trim() && titleEn.length > 100) {
      newErrors.titleEn = "영어 제목은 100자 이내로 입력해주세요.";
    }

    // 링크 URL 형식 검증
    if (
      link &&
      !(function (str) {
        try {
          new URL(str);
          return true;
        } catch {
          return false;
        }
      })(link)
    ) {
      newErrors.link = "유효한 URL을 입력해주세요.";
    }

    setErrors(newErrors);

    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
    };
  }, [titleKo, titleEn, link]);

  /**
   * 안전한 입력 처리 (XSS 방지)
   */
  const handleSafeInput = useCallback(
    (field, value) => {
      // 텍스트 콘텐츠 정화
      const sanitized = sanitizeString(
        value,
        field === "link" ? "url" : "text"
      );

      // XSS 시도 감지
      if (value !== sanitized) {
        logSecurityEvent({
          type: "XSS_ATTEMPT",
          field,
          riskLevel: "HIGH",
          message: "Potential XSS content detected in Popup field",
          originalLength: value.length,
          sanitizedLength: sanitized.length,
        });

        setShowSecurityWarning(true);
      }

      // 필드별 상태 업데이트
      switch (field) {
        case "titleKo":
          setTitleKo(sanitized);
          break;
        case "titleEn":
          setTitleEn(sanitized);
          break;
        case "link":
          setLink(sanitized);
          break;
      }

      // 실시간 검증
      setTimeout(() => validatePopupData(), 300);
    },
    [logSecurityEvent, validatePopupData]
  );

  const handleSaveConfirm = useCallback(async () => {
    setLoading(true);
    try {
      // 1. 클라이언트 검증
      const validation = validatePopupData();

      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        openAlert({ title: "입력 오류", description: firstError });
        setLoading(false);
        return;
      }

      // 2. 보안 위험 감지 시 추가 확인
      if (eventCount > 0 && riskLevel === "HIGH") {
        const confirmResult = window.confirm(
          "입력 데이터에서 보안 위험이 감지되었습니다. 계속 진행하시겠습니까?"
        );
        if (!confirmResult) {
          setLoading(false);
          return;
        }
      }

      // 3. API 요청 데이터 정화 (HTML 콘텐츠는 adminEditor 모드 사용)
      const safePopupData = {
        useState: useStateVal,
        link: sanitizeString(link, "url"),
        title_ko: sanitizeString(titleKo, "text"),
        title_en: sanitizeString(titleEn, "text"),
        content_ko: sanitizeString(
          $("#summernote_ko").summernote("code"),
          "adminEditor"
        ),
        content_en: sanitizeString(
          $("#summernote_en").summernote("code"),
          "adminEditor"
        ),
        addSummerNoteImageArray: addSummerNoteImages,
        delSummerNoteImageArray: delSummerNoteImages,
      };

      // 4. 보안 이벤트 로깅
      if (eventCount > 0) {
        console.warn("Popup save attempt with security events:", {
          eventCount,
          riskLevel,
          action: isEdit ? "update" : "create",
        });
      }

      const result = isEdit
        ? await popupService.updatePopup(id, safePopupData)
        : await popupService.createPopup(safePopupData);

      if (result.success) {
        // 성공 시 보안 로그 정리
        clearSecurityLog();
        openAlert({
          description: result.message,
          response_method: () => navigate("/settings/popup"),
        });
      } else {
        openAlert({ description: result.message });
      }
    } catch (e) {
      console.error("Popup save error:", e);

      // 오류 시 보안 이벤트 로깅
      logSecurityEvent({
        type: "POPUP_SAVE_ERROR",
        field: "form",
        riskLevel: "MEDIUM",
        message: "Popup save process error",
        error: e.message,
      });

      openAlert({ description: "저장 중 오류가 발생했습니다." });
    } finally {
      setLoading(false);
    }
  }, [
    useStateVal,
    link,
    titleKo,
    titleEn,
    isEdit,
    id,
    addSummerNoteImages,
    delSummerNoteImages,
    navigate,
    eventCount,
    riskLevel,
    clearSecurityLog,
    logSecurityEvent,
    validatePopupData,
  ]);

  // Expose for confirm callback
  useEffect(() => {
    window.handleSaveConfirm = handleSaveConfirm;
    return () => {
      delete window.handleSaveConfirm;
    };
  }, [handleSaveConfirm]);

  const handleSubmit = () => {
    const validation = validatePopupData();

    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      openAlert({ description: firstError });
      return;
    }

    openConfirm({
      title: "저장하기",
      description: "작성하신 게시글을 저장하시겠습니까?",
      positive_method: "handleSaveConfirm",
      positive_btn: "확인",
      negative_btn: "취소",
    });
  };

  /**
   * 보안 경고 닫기
   */
  const handleCloseSecurityWarning = () => {
    setShowSecurityWarning(false);
  };

  /**
   * 입력 필드 스타일 (오류 시 강조)
   */
  const getInputClassName = (fieldName) => {
    let className = "input-xxl";

    if (errors[fieldName]) {
      className += " error";
    }

    return className;
  };

  return (
    <SectionContainer
      title={isEdit ? "팝업 수정" : "팝업 등록"}
      className="popup_edit edit write"
    >
      <Spinner visible={loading} />

      {/* 보안 경고 표시 */}
      {showSecurityWarning && (
        <div className="alert alert-warning mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>⚠️ 보안 경고</strong>
              <p className="mb-0">
                입력 데이터에서 의심스러운 패턴이 감지되어 정화되었습니다.
              </p>
              <small>
                보안 이벤트: {eventCount}건, 위험도: {riskLevel}
              </small>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCloseSecurityWarning}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 전체 오류 메시지 */}
      {errors.general && (
        <div className="alert alert-danger mb-3">{errors.general}</div>
      )}

      <div className="content_area">
        <div className="notice">
          <div className="notice_area">
            <div>
              <h3 className="mb-1 tit-h3">사용여부</h3>
              <div className="white-background">
                <div className="radio_button">
                  {[
                    { v: 1, l: "사용" },
                    { v: 0, l: "미사용" },
                  ].map((r) => (
                    <label className="radio_label" key={r.v}>
                      <input
                        className="radio_input"
                        type="radio"
                        name="use-contact"
                        value={r.v}
                        checked={useStateVal === r.v}
                        onChange={() => setUseStateVal(r.v)}
                      />
                      <span>{r.l}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="notice_area">
            <div>
              <h3 className="mb-1 tit-h3">링크삽입</h3>
              <input
                type="text"
                className={getInputClassName("link")}
                value={link}
                onChange={(e) => handleSafeInput("link", e.target.value)}
                placeholder="링크를 입력해주세요."
                maxLength={500}
              />
              {errors.link && (
                <div className="text-danger mt-1 small">{errors.link}</div>
              )}
            </div>
          </div>
          <div className="lang_tab_area">
            {["ko", "en"].map((lang) => (
              <div
                key={lang}
                className={`lang_tab ${selectedLang === lang ? "active" : ""}`}
                data-lang={lang}
                onClick={() => setSelectedLang(lang)}
              >
                {lang === "ko" ? "한국어" : "English"}
              </div>
            ))}
          </div>
          <div className="notice_area">
            <div className="modal-content-wrap">
              {["ko", "en"].map((lang) => (
                <div
                  key={lang}
                  className="lang_content"
                  style={{ display: selectedLang === lang ? "block" : "none" }}
                >
                  <div id={`${lang}_content`}>
                    <h3 className="mb-1 tit-h3">팝업명</h3>
                    <input
                      type="text"
                      className={getInputClassName(
                        lang === "ko" ? "titleKo" : "titleEn"
                      )}
                      value={lang === "ko" ? titleKo : titleEn}
                      onChange={(e) =>
                        handleSafeInput(
                          lang === "ko" ? "titleKo" : "titleEn",
                          e.target.value
                        )
                      }
                      placeholder={
                        lang === "ko"
                          ? "한국어 제목을 입력해주세요."
                          : "Please enter title in English"
                      }
                      maxLength={100}
                    />
                    {errors[lang === "ko" ? "titleKo" : "titleEn"] && (
                      <div className="text-danger mt-1 small">
                        {errors[lang === "ko" ? "titleKo" : "titleEn"]}
                      </div>
                    )}
                    <div className="text-muted small mt-1">
                      {(lang === "ko" ? titleKo : titleEn).length}/100자
                    </div>
                    <h3 className="mb-1 tit-h3">팝업 내용</h3>
                    <div id={`summernote_${lang}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="button_wrap">
            <div className="but-flex">
              <input
                type="button"
                value="취소하기"
                className="btn tertiary mr-1"
                onClick={() => navigate("/settings/popup")}
                disabled={loading}
              />
              <input
                type="button"
                value={loading ? "저장 중..." : "저장하기"}
                className="btn primary"
                onClick={handleSubmit}
                disabled={loading || Object.keys(errors).length > 0}
              />
            </div>
          </div>
        </div>

        {/* 개발 모드에서 보안 상태 표시 */}
        {process.env.NODE_ENV === "development" && eventCount > 0 && (
          <div className="mt-3 p-3 bg-light border rounded">
            <div className="small text-muted">
              <strong>보안 모니터링 상태:</strong>
              <div>
                위험도:{" "}
                <span
                  className={`badge badge-${
                    riskLevel === "HIGH"
                      ? "danger"
                      : riskLevel === "MEDIUM"
                      ? "warning"
                      : "success"
                  }`}
                >
                  {riskLevel}
                </span>
              </div>
              <div>보안 이벤트: {eventCount}건</div>
            </div>
          </div>
        )}
      </div>
    </SectionContainer>
  );
};

export default PopupFormPage;
